/**
 * Vertical stacking of sections (ADR 0041).
 *
 * A wall's sections tile HORIZONTALLY (section-layout.js, ADR 0037). Stacking
 * adds the VERTICAL axis: over one horizontal position a designer can stack
 * units — e.g. a base cabinet, then a unit resting on it, then another. This is
 * pure geometry, floor-up, in the room's unit of measure.
 *
 * Two vertical-placement modes (owner, 2026-07-28):
 *   - a **suspended upper** hangs on the wall, top pinned to the standard tall
 *     panel (placeSectionOnPlatform, 'top' anchor) — it rests on nothing.
 *   - a **stacked** unit RESTS on the top of the unit below it, plus a join GAP.
 *     The gap is the below unit's countertop thickness (when it's a base) or
 *     this unit's capshelf (¾", when it's an upper) — the caller resolves which
 *     and passes it as `gapBelowUnits`.
 *
 * The bottom of a stack is placed by its class anchor (floor / countertop /
 * top) through placeSectionOnPlatform, so stacking composes with platforms
 * (ADR 0040). Each unit above rests on the one below:
 *
 *   bottom = (unit below's top) + gap;  top = bottom + height
 *
 * "To the ceiling" (owner): when `totalTopUnits` is given (e.g. ceiling − crown
 * allowance), the TOP unit AUTOSIZES so the whole stack tops out exactly there.
 * Worked examples: a 95" total on a 35" base → the top unit is 60" (no gap) or
 * 59" (1" countertop, bottom at 36"); a 110" ceiling − 3" crown = 107" total on
 * a 36" base → the top unit is 71".
 *
 * A unit may also carry its OWN `targetTopUnits` — a "to height" pinned on that
 * unit rather than on the stack (owner, 2026-08-04). In a three-tier stack this
 * is what fixes the MIDDLE unit's top: pin the middle to 60" and the unit above
 * it still autosizes from 60" up to the stack target. A unit's own target beats
 * the stack's, because the designer named it explicitly.
 *
 * No-overlap is structural: a stacked unit's bottom is always ≥ the top below
 * (gap ≥ 0). If an autosize can't fit (total below the resting height) the unit
 * gets height 0 and `overflow: true` rather than a negative box.
 */
import { placeSectionOnPlatform } from './platform.js';

/**
 * @param {Array<{anchor?:string, heightUnits:number, gapBelowUnits?:number,
 *   targetTopUnits?:number}>} units
 *   bottom → top. units[0] is the stack base (placed by its anchor); each later
 *   unit rests on the previous one plus its `gapBelowUnits` (0 for the base).
 *   `targetTopUnits` pins that unit's TOP, autosizing its height to reach it.
 * @param {object} ctx { platformUnits?, tallTopUnits?, totalTopUnits? }
 *   totalTopUnits > 0 → the TOP unit autosizes so the stack tops out there.
 * @returns {Array<{bottomUnits:number, topUnits:number, heightUnits:number,
 *   hasLegs:boolean, overflow:boolean, autosized:boolean}>} one per unit, in
 *   the same order. `autosized` is true when the height was solved to a target
 *   rather than taken from the unit — the UI greys those fields.
 */
export function placeVerticalStack(units, ctx = {}) {
  const list = Array.isArray(units) ? units : [];
  const out = [];
  for (let i = 0; i < list.length; i += 1) {
    const u = list[i] ?? {};
    if (i === 0) {
      // The stack base: placed by its class anchor, platform-aware (ADR 0040).
      const p = placeSectionOnPlatform({ heightUnits: u.heightUnits, anchor: u.anchor }, ctx);
      out.push({ ...p, heightUnits: p.topUnits - p.bottomUnits, overflow: false });
      continue;
    }
    const below = out[i - 1];
    const gap = u.gapBelowUnits > 0 ? u.gapBelowUnits : 0;
    const bottom = below.topUnits + gap;
    const isTop = i === list.length - 1;
    // A unit's OWN target wins over the stack's: a designer who pinned this
    // unit to top out at 60" means it, whichever tier it happens to be in.
    // Otherwise only the top unit chases the stack target.
    const targetTop = u.targetTopUnits > 0
      ? u.targetTopUnits
      : (isTop && ctx.totalTopUnits > 0 ? ctx.totalTopUnits : 0);
    let height;
    let overflow = false;
    if (targetTop > 0) {
      height = targetTop - bottom;
      if (height <= 0) { height = 0; overflow = true; }
    } else {
      height = u.heightUnits > 0 ? u.heightUnits : 0;
    }
    out.push({
      bottomUnits: bottom,
      topUnits: bottom + height,
      heightUnits: height,
      hasLegs: false,
      overflow,
      autosized: targetTop > 0,
    });
  }
  return out;
}

/**
 * The join GAP a stacked unit sits above: the below unit's countertop thickness
 * when it is a base (countertop-anchored), otherwise nothing.
 *
 * NOTE a capshelf is NOT a gap. It is a ¾" shelf built onto the BOTTOM of an
 * upper to hide construction seams (owner, 2026-07-28), so it belongs to that
 * unit's own height — a 36" upper with a ¾" capshelf is 36¾" overall — and the
 * unit then rests directly on whatever is below it. Counting it here as well
 * would double it.
 *
 * @param {object} below the unit directly beneath { anchor, countertopUnits }
 */
export function stackGapUnits(below = {}) {
  if (below.anchor === 'countertop' && below.countertopUnits > 0) return below.countertopUnits;
  return 0;
}

/**
 * Does a stacked unit fill its opening edge to edge?
 *
 * This is the SIDE-PANEL question, not a cosmetic one. A flush unit is built
 * with its side boards resting on the boards below it — one unbroken panel runs
 * from the floor to the top of the stack — so the run's divider simply rises
 * through it and the unit adds no panel of its own. A unit genuinely narrower
 * than what it rests on (an 18" unit on a 28" base) carries its own two side
 * panels, standing on the base's top between the base's panels.
 *
 * Drawing a flush unit's panels as its own put them INBOARD of the dividers
 * below, leaving the base's ¾" sticking out beside the stacked unit (owner,
 * 2026-07-29) — physically impossible, and the reason this test exists.
 *
 * @param {{widthUnits?:number, offsetUnits?:number}} unit placed unit
 * @param {number} openingUnits the run opening it sits over
 * @param {number} [tol] slack for float arithmetic
 */
export function isFlushUnit(unit, openingUnits, tol = 0.01) {
  if (!unit || !(openingUnits > 0)) return false;
  const width = unit.widthUnits > 0 ? unit.widthUnits : openingUnits;
  return (unit.offsetUnits ?? 0) <= tol && width >= openingUnits - tol;
}

/**
 * The vertical extent of the CONTINUOUS carcass over one opening: the run
 * section plus every flush unit stacked on it. The first non-flush unit ends the
 * walk — a narrower unit breaks the panel line, so nothing above it can be
 * carried by this opening's dividers either.
 *
 * @param {Array<{bottomUnits:number, topUnits:number, heightUnits?:number}>} units bottom → top
 * @param {number} openingUnits
 * @returns {{bottomUnits:number, topUnits:number}|null} null for an empty stack
 */
export function carcassExtent(units, openingUnits, tol = 0.01) {
  const list = Array.isArray(units) ? units : [];
  const run = list[0];
  if (!run) return null;
  let topUnits = run.topUnits;
  for (let i = 1; i < list.length; i += 1) {
    const u = list[i];
    if (!(u.heightUnits > 0) || !isFlushUnit(u, openingUnits, tol)) break;
    if (u.topUnits > topUnits) topUnits = u.topUnits;
  }
  return { bottomUnits: run.bottomUnits, topUnits };
}
